import { FC, ReactElement, useCallback, useState } from "react";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import { Theme as MuiTheme } from "@mui/material/styles";

import { logout_icon, logout_red_icon } from "@/constants";
import { LogoutConfirmModal } from "@/components";
import { LogoutIcon } from "@/components/sidebar-icons";
import { useAppDispatch } from "@/store";
import { logout } from "@/services";
import { vwScale } from "@/utils/responsive";
import { responsiveFontSizes } from "@/utils/responsive";
import { useNavigate } from "react-router-dom";

interface ILogoutItem {
  navbar?: boolean;
  isSidebarExtended?: boolean;
}

export const LogoutItem: FC<ILogoutItem> = ({
  navbar,
  isSidebarExtended,
}): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const mdUp = useMediaQuery((theme: MuiTheme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
    noSsr: false,
  });

  const extendedSidebar = useCallback(() => {
    if (mdUp) {
      return isSidebarExtended;
    }
    return false;
  }, [mdUp, isSidebarExtended]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleLogoutModalOpen = () => {
    setOpenModal(true);
  };

  const handleLogoutModalClose = () => {
    setOpenModal(false);
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        px: extendedSidebar() ? 2 : 0,
        pb: 1,
      }}
    >
      {navbar ? (
        <Button
          color="error"
          fullWidth
          onClick={handleLogoutModalOpen}
          sx={{ justifyContent: "flex-start" }}
        >
          <img src={logout_red_icon} alt="Logout" width={17} />
          <Typography
            sx={{
              fontWeight: 600,
              lineHeight: vwScale(20, 16),
              fontSize: responsiveFontSizes.base,
              ml: 1.5,
            }}
          >
            Logout
          </Typography>
        </Button>
      ) : !mdUp ? (
        <Button
          variant="outlined"
          sx={{
            borderColor: "#8E9494",
            borderRadius: "8px",
            justifyContent: "flex-start",
          }}
          onClick={handleLogoutModalOpen}
        >
          <img src={logout_icon} alt="Logout" width={17} />
          <Typography
            sx={{
              fontWeight: 600,
              lineHeight: vwScale(20, 16),
              fontSize: responsiveFontSizes.base,
              ml: 1.5,
            }}
          >
            Logout
          </Typography>
        </Button>
      ) : (
        <Stack
          direction={extendedSidebar() ? "row" : "column"}
          alignItems={"center"}
          justifyContent={extendedSidebar() ? "flex-start" : "center"}
          sx={{
            borderRadius: "5px",
            width: extendedSidebar() ? "93%" : "64px",
            height: extendedSidebar() ? "37px" : "64px",
            mx: extendedSidebar() ? "7px" : "auto",
            cursor: "pointer",
            pl: extendedSidebar() ? 2.5 : 0.5,
            my: extendedSidebar() ? 0.2 : 0,
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              background: (theme) => theme.palette.action.hover,
            },
          }}
          spacing={extendedSidebar() ? 1 : 0.5}
          onClick={handleLogoutModalOpen}
        >
          <Box sx={{ width: extendedSidebar() ? 18 : 16, height: 'auto' }}>
            <LogoutIcon color="#8e9494" />
          </Box>
          <Typography
            color={"#8e9494"}
            sx={{
              fontWeight: 500,
              lineHeight: "normal",
              fontSize: extendedSidebar() ? "16px" : responsiveFontSizes.xs,
              letterSpacing: "0px",
            }}
          >
            Logout
          </Typography>
        </Stack>
      )}

      {/* Logout modal */}
      <LogoutConfirmModal
        open={openModal}
        onClose={handleLogoutModalClose}
        maxWidth="xs"
        onConfirm={handleLogout}
      />
    </Box>
  );
};
